// Root: auth gate + routing. Signed-out → login. Signed-in → the role-gated
// shell. W2 fills the member-facing pages; chat (W3), leader (W4), admin (W5)
// and Learn/Tier B remain placeholders.
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { RoleProvider } from './app/RoleContext';
import AppShell from './components/AppShell';
import RequireRole from './components/RequireRole';
import RequireAuthor from './components/RequireAuthor';
import Placeholder from './components/Placeholder';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import HomePage from './pages/HomePage';
import LearnerResponsesPage from './pages/LearnerResponsesPage';
import SearchResultsPage from './pages/SearchResultsPage';
import NotificationsPage from './pages/NotificationsPage';
import GroupsPage from './pages/GroupsPage';
import GroupDetailPage from './pages/GroupDetailPage';
import NewGroupPage from './pages/NewGroupPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import EventRegisterPage from './pages/EventRegisterPage';
// EventChatPage retired (design/024) — chat removed from Events.
import NewEventPage from './pages/NewEventPage';
import InvitesPage from './pages/InvitesPage';
import ProfilePage from './pages/ProfilePage';
import BillingPage from './pages/BillingPage';
import { TermsPage, PrivacyPage, DeleteAccountPage, SupportPage } from './pages/LegalPage';
import UnsubscribePage from './pages/UnsubscribePage';
import RegistrationPage from './pages/RegistrationPage';
import SuspendedPage from './pages/SuspendedPage';
import LeaderGroupPage from './pages/LeaderGroupPage';
import LeaderEventsPage from './pages/LeaderEventsPage';
// LeaderInvitesPage retired — invites now live under Manage members (design/022 UX consolidation).
import CoursesBrowsePage from './pages/CoursesBrowsePage';
import PracticesBrowsePage from './pages/PracticesBrowsePage';
import LearnCoursePage from './pages/LearnCoursePage';
import LessonPlayerPage from './pages/LessonPlayerPage';
import PracticePlayerPage from './pages/PracticePlayerPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminMembersPage from './pages/AdminMembersPage';
import AdminModerationPage from './pages/AdminModerationPage';
import CoursesAdminPage from './pages/admin/CoursesAdminPage';
import CourseLessonsAdminPage from './pages/admin/CourseLessonsAdminPage';
import LessonEditorPage from './pages/admin/LessonEditorPage';
import PracticesAdminPage from './pages/admin/PracticesAdminPage';
import PracticeEditorPage from './pages/admin/PracticeEditorPage';
import MembersAdminPage from './pages/admin/MembersAdminPage';
import RequireMemberManager from './components/RequireMemberManager';

export default function App() {
  const { user, me, initializing } = useAuth();
  const { pathname } = useLocation();

  // Public legal pages (design/025) — linked from every paywall and from the
  // App Store / Play listings, so they must open without signing in.
  if (pathname === '/terms') return <TermsPage />;
  if (pathname === '/privacy') return <PrivacyPage />;
  if (pathname === '/delete-account') return <DeleteAccountPage />;
  if (pathname === '/support') return <SupportPage />;
  // design/035 — unsubscribe link from announcement emails (no sign-in).
  if (pathname === '/unsubscribe') return <UnsubscribePage />;

  if (initializing) {
    return (
      <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>
        Loading…
      </div>
    );
  }

  if (!user) return <LoginPage />;

  // Invite-provisioned accounts must set a real password before anything else
  // (design/018 Phase 4). Block the whole app until the flag clears.
  if (me?.mustChangePassword) return <ChangePasswordPage />;

  // design/027 — paid registration: signed in, but no account until the
  // free trial starts. The registration page is the whole app until then.
  if (me && me.provisioned === false) return <RegistrationPage />;

  // design/036 — suspended: only the suspended screen (why, sign out, delete).
  if (me?.suspended) return <SuspendedPage />;

  return (
    <RoleProvider>
      <Routes>
        <Route element={<AppShell />}>
          {/* Events (W2 — formerly "Practices") */}
          <Route index element={<HomePage />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="groups/new" element={<NewGroupPage />} />
          <Route path="group/:id" element={<GroupDetailPage />} />
          <Route path="group/:id/register" element={<EventRegisterPage kind="group" />} />
          <Route path="events" element={<EventsPage />} />
          {/* New Event lives under /leader/ so the sidebar highlights Manager
              Tools → Events (not the Home catalog entry) while the form is
              open. Old bookmarks to /events/new get redirected. */}
          <Route path="events/new" element={<Navigate to="/leader/events/new" replace />} />
          <Route path="leader/events/new" element={<RequireRole min="events"><NewEventPage /></RequireRole>} />
          {/* Event management route (design/022) — owner-only inline UI +
              Invite panel. /event/:id remains view-only for everyone. */}
          <Route path="leader/event/:id" element={<EventDetailPage manageMode />} />
          <Route path="event/:id" element={<EventDetailPage />} />
          <Route path="event/:id/register" element={<EventRegisterPage />} />
          <Route path="event/:id/chat" element={<Navigate to="/events" replace />} />
          {/* Legacy /practice(s) → /events redirects retired (design/019 reclaimed
              the "Practice" noun for a Learn feature; /practices is now its own
              catalog page). Old bookmarks to /events content still work through
              the current /event/:id routes. */}
          <Route path="invites" element={<InvitesPage />} />

          {/* Account (W2) */}
          <Route path="profile" element={<ProfilePage />} />
          <Route path="billing" element={<BillingPage />} />

          {/* Learn (Tier B — design 009 + 019 + 020).
              /library retired — split into /courses and /practices. */}
          <Route path="courses" element={<CoursesBrowsePage />} />
          <Route path="practices" element={<PracticesBrowsePage />} />
          <Route path="learn/course/:id" element={<LearnCoursePage />} />
          <Route path="learn/lesson/:id" element={<LessonPlayerPage />} />
          <Route path="learn/practice/:id" element={<PracticePlayerPage />} />

          {/* Leader tools (W4) */}
          <Route path="leader/group" element={<RequireRole min="groups"><LeaderGroupPage /></RequireRole>} />
          {/* Management detail (design/021): view+full-admin controls for a
              specific group. /group/:id is view-only for everyone. */}
          <Route path="leader/group/:id" element={<RequireRole min="groups"><GroupDetailPage manageMode /></RequireRole>} />
          <Route path="leader/events" element={<RequireRole min="events"><LeaderEventsPage /></RequireRole>} />
          <Route path="leader/practices" element={<Navigate to="/leader/events" replace />} />
          {/* Old Send-invites page retired; redirect for stale bookmarks. */}
          <Route path="leader/invites" element={<Navigate to="/admin/learn-members" replace />} />

          {/* Admin (W5 / Tier B) */}
          <Route path="admin" element={<RequireRole min="super"><AdminDashboardPage /></RequireRole>} />
          <Route path="admin/courses" element={<RequireAuthor><CoursesAdminPage /></RequireAuthor>} />
          <Route path="admin/courses/:id" element={<RequireAuthor><CourseLessonsAdminPage /></RequireAuthor>} />
          <Route path="admin/content/lesson/:id" element={<RequireAuthor><LessonEditorPage /></RequireAuthor>} />
          {/* Practices (design/019) */}
          <Route path="admin/practices" element={<RequireAuthor><PracticesAdminPage /></RequireAuthor>} />
          <Route path="admin/practices/:id" element={<RequireAuthor><PracticeEditorPage /></RequireAuthor>} />
          <Route path="admin/learn-members" element={<RequireMemberManager><MembersAdminPage /></RequireMemberManager>} />
          <Route path="learner-responses" element={<RequireAuthor><LearnerResponsesPage /></RequireAuthor>} />
          <Route path="admin/members" element={<RequireRole min="super"><AdminMembersPage /></RequireRole>} />
          <Route path="admin/moderation" element={<RequireRole min="super"><AdminModerationPage /></RequireRole>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </RoleProvider>
  );
}
